/**
 * Category Tag Picker Component
 * Multi-select chip-based UI for feedback categories with "Other" text input
 */

import React from 'react';
import { View, Text, TextInput, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { FeedbackCategory, FEEDBACK_CATEGORIES } from '@/types/feedback.types';

interface CategoryTagPickerProps {
  selectedCategories: FeedbackCategory[];
  onCategoriesChange: (categories: FeedbackCategory[]) => void;
  customCategoryText: string;
  onCustomTextChange: (text: string) => void;
  disabled?: boolean;
}

const MAX_CUSTOM_TEXT_LENGTH = 100;

export const CategoryTagPicker: React.FC<CategoryTagPickerProps> = ({
  selectedCategories,
  onCategoriesChange,
  customCategoryText,
  onCustomTextChange,
  disabled,
}) => {
  const isOtherSelected = selectedCategories.includes('other');

  const toggleCategory = (category: FeedbackCategory) => {
    if (disabled) return;

    if (selectedCategories.includes(category)) {
      // Remove category
      onCategoriesChange(selectedCategories.filter(c => c !== category));
      // Clear custom text if "other" is deselected
      if (category === 'other') {
        onCustomTextChange('');
      }
    } else {
      // Add category
      onCategoriesChange([...selectedCategories, category]);
    }
  };

  const showCustomTextCounter = customCategoryText.length >= 80;
  const remainingChars = MAX_CUSTOM_TEXT_LENGTH - customCategoryText.length;

  return (
    <View style={styles.container}>
      <Text style={styles.label}>
        Categories <Text style={styles.required}>*</Text>
        <Text style={styles.hint}> (Select all that apply)</Text>
      </Text>

      {/* Category Tags Grid */}
      <View style={styles.tagsContainer}>
        {FEEDBACK_CATEGORIES.map(category => {
          const isSelected = selectedCategories.includes(category.value);

          return (
            <TouchableOpacity
              key={category.value}
              style={[styles.tag, isSelected && styles.tagSelected, disabled && styles.tagDisabled]}
              onPress={() => toggleCategory(category.value)}
              disabled={disabled}
              activeOpacity={0.7}
            >
              <Ionicons
                name="pricetag"
                size={16}
                color={isSelected ? Colors.accent.primary : Colors.text.tertiary}
              />
              <Text
                style={[
                  styles.tagText,
                  isSelected && styles.tagTextSelected,
                  disabled && styles.tagTextDisabled,
                ]}
              >
                {category.label}
              </Text>
              {isSelected && (
                <Ionicons name="checkmark-circle" size={16} color={Colors.accent.primary} />
              )}
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Custom Text Input for "Other" */}
      {isOtherSelected && (
        <View style={styles.customTextContainer}>
          <Text style={styles.customTextLabel}>
            Specify other category <Text style={styles.required}>*</Text>
          </Text>
          <TextInput
            style={styles.customTextInput}
            placeholder="E.g., Onboarding, Notifications, Settings..."
            placeholderTextColor={Colors.text.tertiary}
            value={customCategoryText}
            onChangeText={onCustomTextChange}
            maxLength={MAX_CUSTOM_TEXT_LENGTH}
            editable={!disabled}
          />
          {showCustomTextCounter && (
            <Text style={[styles.counter, remainingChars < 20 && styles.counterWarning]}>
              {remainingChars} characters remaining
            </Text>
          )}
        </View>
      )}

      {/* Selected Count Helper */}
      {selectedCategories.length > 0 && (
        <View style={styles.selectionInfo}>
          <Ionicons name="checkmark-circle" size={14} color={Colors.status.success} />
          <Text style={styles.selectionInfoText}>
            {selectedCategories.length}{' '}
            {selectedCategories.length === 1 ? 'category' : 'categories'} selected
          </Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.text.primary,
    marginBottom: 12,
  },
  required: {
    color: Colors.status.error,
  },
  hint: {
    fontSize: 12,
    fontWeight: '400',
    color: Colors.text.tertiary,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: Colors.background.tertiary,
    borderRadius: 20,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderWidth: 1.5,
    borderColor: Colors.border.subtle,
  },
  tagSelected: {
    backgroundColor: `${Colors.accent.primary}15`, // 15% opacity
    borderColor: Colors.accent.primary,
  },
  tagDisabled: {
    opacity: 0.5,
  },
  tagText: {
    fontSize: 14,
    color: Colors.text.primary,
    fontWeight: '500',
  },
  tagTextSelected: {
    color: Colors.accent.primary,
    fontWeight: '600',
  },
  tagTextDisabled: {
    color: Colors.text.tertiary,
  },
  customTextContainer: {
    marginTop: 16,
    padding: 12,
    backgroundColor: Colors.background.tertiary,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.accent.primary,
  },
  customTextLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: Colors.text.primary,
    marginBottom: 8,
  },
  customTextInput: {
    backgroundColor: Colors.background.primary,
    borderRadius: 8,
    padding: 12,
    fontSize: 15,
    color: Colors.text.primary,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  counter: {
    fontSize: 11,
    color: Colors.text.tertiary,
    textAlign: 'right',
    marginTop: 4,
  },
  counterWarning: {
    color: Colors.status.warning,
    fontWeight: '600',
  },
  selectionInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 12,
    paddingVertical: 6,
  },
  selectionInfoText: {
    fontSize: 12,
    color: Colors.text.secondary,
    fontWeight: '500',
  },
});
